/**
 * Frequency cap and content for the help assistant's nudge (D10-UI-008, #4601).
 *
 * The nudge is a small, occasional card beside the help bubble. Whether it may
 * show is decided here from a per-user record in localStorage (one browser's
 * memory is enough for the cap -- it is a hint, not state the server needs):
 *
 *   - at most once per browser session (sessionStorage flag);
 *   - never in a session in which the user has already opened the chat;
 *   - never within HELP_NUDGE_SHOW_GAP_MS of the last time it was shown;
 *   - never within HELP_NUDGE_DISMISS_COOLDOWN_MS of the user closing it.
 *
 * What it says depends on whether the user has used the chat: opened it in
 * this browser (the record's `usedAt`) OR, per the server, ever asked it
 * anything on any device (`has_used_chat` on the status payload) -- so someone
 * who chatted elsewhere, or before the nudge existed, gets tips, not the
 * "never used" lines:
 *
 *   - Never used: a rotating pool of "value" lines (HELP_NUDGE_POOL) --
 *     "Need any help?" first, then short lines showing one thing the
 *     assistant really does (D10-ACT: explain steps, point at a control, open
 *     a page, start a tour, open the problem-report form; D10-VOX: voice
 *     notes). A line whose capability this user lacks (no tour they can open,
 *     voice notes off) is never shown.
 *   - Has used it: a "Did you know?" tip asking one of the user's own
 *     persona's suggested questions (D10-UI-007's generated pools), cut to the
 *     pages this user can open -- the same allowed routes the panel sends with
 *     every turn, so a tip never names a surface outside their permissions.
 *
 * Both rotate in order across showings (the last one shown is kept in the same
 * record) and never repeat back to back.
 *
 * Storage failures (private mode, quota) fail SAFE: an unreadable record means
 * "do not show", so a broken store can never turn the nudge into a nag.
 */
import { suggestedQuestions, suggestionPersona } from "./suggestions";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Seconds of no keyboard/pointer activity on one page before it may show. */
export const HELP_NUDGE_IDLE_MS = 45_000;
/** It slides back on its own after this long (paused while hovered or focused). */
export const HELP_NUDGE_AUTO_HIDE_MS = 9_000;
/** Length of the slide-back (`.crm-overlay-exit`) before it unmounts. */
export const HELP_NUDGE_EXIT_MS = 200;
/** Minimum gap between two showings, even when the first was ignored. */
export const HELP_NUDGE_SHOW_GAP_MS = 3 * DAY_MS;
/** Quiet period after the user closes it with the X. */
export const HELP_NUDGE_DISMISS_COOLDOWN_MS = 7 * DAY_MS;

export interface HelpNudgeRecord {
  shownAt?: number;
  dismissedAt?: number;
  /** Last time the user opened the chat (any route). Set = "has used it". */
  usedAt?: number;
  /** Id of the value line shown last. */
  lastLine?: string;
  /** Question text of the tip shown last. */
  lastTip?: string;
}

export function helpNudgeStorageKey(userId: number): string {
  return `support-help-nudge:v1:${userId}`;
}

export function helpNudgeSessionKey(userId: number): string {
  return `support-help-nudge:session:v1:${userId}`;
}

export function helpNudgeUsedSessionKey(userId: number): string {
  return `support-help-nudge:used:v1:${userId}`;
}

function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

/** The stored record, `{}` when none, or null when storage cannot be read. */
export function readHelpNudgeRecord(userId: number): HelpNudgeRecord | null {
  try {
    const raw = window.localStorage.getItem(helpNudgeStorageKey(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const o = parsed as Record<string, unknown>;
    return {
      shownAt: num(o.shownAt),
      dismissedAt: num(o.dismissedAt),
      usedAt: num(o.usedAt),
      lastLine: str(o.lastLine),
      lastTip: str(o.lastTip),
    };
  } catch {
    return null;
  }
}

function update(userId: number, patch: HelpNudgeRecord): void {
  try {
    const current = readHelpNudgeRecord(userId) ?? {};
    window.localStorage.setItem(
      helpNudgeStorageKey(userId),
      JSON.stringify({ ...current, ...patch }),
    );
  } catch {
    /* storage unavailable -- nothing to remember */
  }
}

/** True / false, or null when sessionStorage cannot be read. */
function sessionFlag(key: string): boolean | null {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return null;
  }
}

function setSessionFlag(key: string): void {
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

/** Pure decision over a record (exported for tests). */
export function nudgeAllowedByRecord(record: HelpNudgeRecord, now: number): boolean {
  const within = (at: number | undefined, gap: number) =>
    at !== undefined && now - at >= 0 && now - at < gap;
  // A timestamp in the future (clock moved back) is treated as recent too.
  const future = (at: number | undefined) => at !== undefined && at > now;
  if (future(record.shownAt) || future(record.dismissedAt)) return false;
  if (within(record.shownAt, HELP_NUDGE_SHOW_GAP_MS)) return false;
  if (within(record.dismissedAt, HELP_NUDGE_DISMISS_COOLDOWN_MS)) return false;
  return true;
}

/** May the nudge show for this user right now? Fails safe (false) on storage errors. */
export function canShowHelpNudge(userId: number, now: number = Date.now()): boolean {
  if (sessionFlag(helpNudgeSessionKey(userId)) !== false) return false;
  if (sessionFlag(helpNudgeUsedSessionKey(userId)) !== false) return false;
  const record = readHelpNudgeRecord(userId);
  if (record === null) return false;
  return nudgeAllowedByRecord(record, now);
}

/** Records a showing of `content` (spends this session's one showing). */
export function markHelpNudgeShown(
  userId: number,
  content?: HelpNudgeContent,
  now: number = Date.now(),
): void {
  setSessionFlag(helpNudgeSessionKey(userId));
  const patch: HelpNudgeRecord = { shownAt: now };
  if (content?.kind === "value") patch.lastLine = content.id;
  if (content?.kind === "tip") patch.lastTip = content.question;
  update(userId, patch);
}

export function markHelpNudgeDismissed(userId: number, now: number = Date.now()): void {
  update(userId, { dismissedAt: now });
}

/** Called whenever the user opens the chat panel, by any route. */
export function markHelpChatUsed(userId: number, now: number = Date.now()): void {
  setSessionFlag(helpNudgeUsedSessionKey(userId));
  update(userId, { usedAt: now });
}

// ---------------------------------------------------------------- content

/** What this user can actually get from the assistant right now. */
export interface HelpNudgeCaps {
  /** At least one guided tour this user can open (start_tour). */
  tours: boolean;
  /** Voice notes are switched on and this browser can record. */
  voice: boolean;
}

export interface HelpNudgeContent {
  kind: "value" | "tip";
  /** Pool line id (value) or the question text (tip). */
  id: string;
  title: string;
  /** Always names the assistant AND says it is AI. */
  body: string;
  /** Tips only: the question pre-filled in the chat input on click. */
  question?: string;
}

interface PoolEntry {
  id: string;
  title: string;
  body: (name: string) => string;
  needs?: keyof HelpNudgeCaps;
}

/**
 * The value-line rotation for a user who has never opened the chat, in order.
 * Only claims the assistant really makes good on (see
 * docs/Beyond_Support_Assistant_Guide.md, "What it can help with").
 */
export const HELP_NUDGE_POOL: readonly PoolEntry[] = [
  { id: "need-help", title: "Need any help?", body: (n) => `Ask ${n}, our AI assistant` },
  {
    id: "how-to",
    title: "Not sure how to do something?",
    body: (n) => `Ask our AI assistant ${n} and it will show you`,
  },
  {
    id: "point",
    title: "Can't find a button?",
    body: (n) => `Our AI assistant ${n} can point straight at it`,
  },
  {
    id: "tour",
    title: "Want a walkthrough?",
    body: (n) => `Our AI assistant ${n} can start a guided tour for you`,
    needs: "tours",
  },
  {
    id: "take-me",
    title: "Looking for a page?",
    body: (n) => `Ask our AI assistant ${n} to take you there`,
  },
  {
    id: "voice",
    title: "Prefer talking?",
    body: (n) => `Ask our AI assistant ${n} with a voice note`,
    needs: "voice",
  },
  {
    id: "report",
    title: "Something not working?",
    body: (n) => `Our AI assistant ${n} can help you report it`,
  },
];

/** The value lines this user may be shown, in rotation order. */
export function helpNudgeLines(name: string, caps: HelpNudgeCaps): HelpNudgeContent[] {
  return HELP_NUDGE_POOL.filter((e) => !e.needs || caps[e.needs]).map((e) => ({
    kind: "value" as const,
    id: e.id,
    title: e.title,
    body: e.body(name),
  }));
}

/** The "Did you know?" tips for this persona, only on pages in `allowedRoutes`. */
export function helpNudgeTips(
  name: string,
  role: string | null | undefined,
  preset: string | null | undefined,
  allowedRoutes: readonly string[],
): HelpNudgeContent[] {
  // currentRoute "" keeps the generated pool's own order (a stable rotation).
  return suggestedQuestions(suggestionPersona(role, preset), allowedRoutes, "").map((q) => ({
    kind: "tip" as const,
    id: q,
    title: "Did you know?",
    body: `Ask our AI assistant ${name}: "${q}"`,
    question: q,
  }));
}

/**
 * The item after `lastId` in `order`, skipping ids not in `items` and never
 * `lastId` itself (unless it is the only item). No or unknown history -> the
 * first item.
 */
export function nextInRotation(
  items: HelpNudgeContent[],
  order: readonly string[],
  lastId: string | undefined,
): HelpNudgeContent | null {
  if (items.length === 0) return null;
  const from = lastId === undefined ? -1 : order.indexOf(lastId);
  if (from < 0) return items[0]!;
  for (let step = 1; step <= order.length; step++) {
    const id = order[(from + step) % order.length];
    if (id === lastId) continue;
    const item = items.find((i) => i.id === id);
    if (item) return item;
  }
  return items[0]!;
}

export interface HelpNudgeInputs {
  name: string;
  role: string | null | undefined;
  preset: string | null | undefined;
  allowedRoutes: readonly string[];
  caps: HelpNudgeCaps;
  /** The server says this user has asked the assistant something before. */
  usedOnServer?: boolean;
}

/**
 * What to show next for this record: a tip for someone who has used the chat
 * (falling back to the value lines when their persona has no tip on a page
 * they can open), otherwise the next value line.
 */
export function pickHelpNudgeContent(
  record: HelpNudgeRecord,
  inputs: HelpNudgeInputs,
): HelpNudgeContent | null {
  const lines = helpNudgeLines(inputs.name, inputs.caps);
  if (record.usedAt !== undefined || inputs.usedOnServer === true) {
    const tips = helpNudgeTips(inputs.name, inputs.role, inputs.preset, inputs.allowedRoutes);
    const tip = nextInRotation(
      tips,
      tips.map((t) => t.id),
      record.lastTip,
    );
    if (tip) return tip;
  }
  return nextInRotation(
    lines,
    HELP_NUDGE_POOL.map((e) => e.id),
    record.lastLine,
  );
}

/**
 * The idle delay. A development build may shorten it with
 * `VITE_SUPPORT_NUDGE_DELAY_MS` so the nudge can be screenshotted; a
 * production build always uses HELP_NUDGE_IDLE_MS.
 */
export function helpNudgeIdleMs(): number {
  if (import.meta.env.DEV) {
    const raw = Number(import.meta.env.VITE_SUPPORT_NUDGE_DELAY_MS);
    if (Number.isFinite(raw) && raw > 0) return raw;
  }
  return HELP_NUDGE_IDLE_MS;
}
